import { t } from 'i18next'
import { type FC, useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import useTracksStore, { TrackType } from '../../shared/store/tracks.store.tsx'
import useUiStore from '../../shared/store/ui.store.tsx'
import { addMediaFileTrack } from '@/shared/store/use-cases/add-media-files-track.ts'
import Button from '../common/Button.tsx'
import Icon from '../common/Icon.tsx'
import PlaybackLine from './PlaybackLine.tsx'
import TimelineBar from './TimelineBar'
import TrackControls from './TrackControls.tsx'
import MediaFileTrack from './Tracks/MediaFileTrack.tsx'
import SoundDesignTrack from './Tracks/SoundDesignTrack.tsx'
import SoundEffectTrack from './Tracks/SoundEffectTrack.tsx'
import VoiceTrack from './Tracks/VoiceTrack.tsx'
import HorizontalScrollArea from '../common/HorizontalScrollArea.tsx'
import VerticalScrollArea from '../common/VerticalScrollArea.tsx'

const TrackComponents = {
  [TrackType.Voices]: VoiceTrack,
  [TrackType.SoundDesigns]: SoundDesignTrack,
  [TrackType.MediaFiles]: MediaFileTrack,
  [TrackType.SoundEffects]: SoundEffectTrack,
} as const

const MainEditor: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const { pixelsPerSecond, zoom } = useUiStore(
    useShallow((state) => ({
      pixelsPerSecond: state.pixelsPerSecond,
      zoom: state.zoom,
    }))
  )

  const {
    divisionsPerSecond,
    setDivisionsPerSecond,
    tracks,
    maxProjectDuration,
  } = useTracksStore(
    useShallow((state) => ({
      divisionsPerSecond: state.divisionsPerSecond,
      setDivisionsPerSecond: state.setDivisionsPerSecond,
      tracks: state.tracks,
      maxProjectDuration: state.maxProjectDuration,
    }))
  )

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)

  const editorPadding = 32

  const getDivisionPerSecond = (zoom: number): number => {
    if (zoom >= 1.33) return 4
    if (zoom >= 0.75) return 2
    return 1
  }

  useEffect(() => {
    setDivisionsPerSecond(getDivisionPerSecond(zoom))
  }, [zoom])

  useEffect(() => {
    const scrollEl = scrollContainerRef.current
    const timelineEl = timelineRef.current

    if (!scrollEl || !timelineEl) return

    let isSyncingScroll = false

    const syncScrollLeft = (source: 'timeline' | 'scrollArea') => {
      if (isSyncingScroll) return
      isSyncingScroll = true

      if (source === 'scrollArea') {
        timelineEl.scrollLeft = scrollEl.scrollLeft
      } else {
        scrollEl.scrollLeft = timelineEl.scrollLeft
      }

      requestAnimationFrame(() => {
        isSyncingScroll = false
      })
    }

    const handleScrollAreaScroll = () => syncScrollLeft('scrollArea')
    const handleTimelineScroll = () => syncScrollLeft('timeline')

    scrollEl.addEventListener('scroll', handleScrollAreaScroll)
    timelineEl.addEventListener('scroll', handleTimelineScroll)

    return () => {
      scrollEl.removeEventListener('scroll', handleScrollAreaScroll)
      timelineEl.removeEventListener('scroll', handleTimelineScroll)
    }
  }, [])

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex w-full">
        <div className="relative min-w-64">
          <div className="flex h-8 min-w-64 items-center bg-zinc-800 px-2 text-sm font-semibold text-zinc-500">
            {t('ls_tracks_bar_tracks')}
          </div>
        </div>
        <div
          className="scrollbar-none relative flex w-full overflow-x-auto"
          ref={timelineRef}
        >
          <div
            style={{
              minWidth:
                maxProjectDuration * pixelsPerSecond + editorPadding + 1,
            }}
          >
            <div className="flex">
              <div className="h-8 min-w-4 border-b border-b-gray-800 bg-zinc-800" />
              <TimelineBar divisionsPerSecond={divisionsPerSecond} />
              <div className="h-8 min-w-4 border-b border-b-gray-800 bg-zinc-800" />
            </div>
          </div>
        </div>
      </div>

      <VerticalScrollArea
        side="left"
        className="min-h-0 flex-1 overflow-hidden"
      >
        <div className="flex min-h-0 flex-1 pb-4">
          <div className="relative min-w-64">
            {tracks.map((track, index) => {
              const soundDesignTracksSeen = tracks
                .slice(0, index + 1)
                .filter((t) => t.type === TrackType.MediaFiles)

              const hasOptions =
                track.type === TrackType.MediaFiles &&
                soundDesignTracksSeen.length > 1

              return (
                <TrackControls
                  key={track.id}
                  trackId={track.id}
                  hasOptions={hasOptions}
                />
              )
            })}
            <Button
              disabled={
                isLoading ||
                tracks.filter((track) => track.type === TrackType.MediaFiles)
                  .length >= 3
              }
              className="mt-4 !bg-transparent px-4"
              fullWidth
              onClick={addMediaFileTrack}
            >
              <Icon name="add" width={16} />
              <span>{t('ls_tracks_add_media_track')}</span>
            </Button>
          </div>

          <HorizontalScrollArea
            className="max-w-[calc(100%-256px)] bg-zinc-700"
            ref={scrollContainerRef}
          >
            <div
              style={{
                width: `${maxProjectDuration * pixelsPerSecond + editorPadding + 1}px`,
              }}
            >
              <div className="relative flex flex-col">
                <PlaybackLine />
                {tracks.map(({ id, type }) => {
                  const TrackComponent = TrackComponents[type]
                  if (!TrackComponent) return null
                  const ref =
                    type === TrackType.Voices ? scrollContainerRef : undefined
                  return <TrackComponent key={id} trackId={id} ref={ref} />
                })}
              </div>
            </div>
          </HorizontalScrollArea>
        </div>
      </VerticalScrollArea>
    </div>
  )
}

export default MainEditor
